import { useEffect, useState } from "react";
import Modal from "../../../../componentes/modal/modal";
import Button from "../../../../componentes/button/button";
import Input from "../../../../componentes/input/input";
import CadastrarFornecedorModal from "./CadastrarFornecedorModal";
import FornecedorService from "../../../../services/resource/forncedorService";
import { formatarCnpjCpf } from "../../../../utils/solicitacoes/formatters";

const fornecedorService = new FornecedorService();
const DEBOUNCE_MS = 300;
const TAMANHO_MINIMO_BUSCA = 2;

function useDebounced(valor, delay) {
    const [debounced, setDebounced] = useState(valor);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(valor), delay);
        return () => clearTimeout(t);
    }, [valor, delay]);
    return debounced;
}

/**
 * Busca de fornecedor via /autocomplete (server-side, Razão Social + Nome Fantasia + CPF/CNPJ,
 * limitado a 10 resultados pelo backend) — a lista completa de fornecedores é grande demais para
 * carregar e filtrar no cliente sem travar o modal.
 */
export default function SelecionarFornecedorModal({ isOpen, onClose, onSelecionar }) {
    const [termo, setTermo] = useState("");
    const [resultados, setResultados] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [modalCadastroAberto, setModalCadastroAberto] = useState(false);

    const termoDebounced = useDebounced(termo, DEBOUNCE_MS);

    useEffect(() => {
        if (!isOpen) return;
        setTermo("");
        setResultados([]);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || termoDebounced.trim().length < TAMANHO_MINIMO_BUSCA) {
            setResultados([]);
            return undefined;
        }

        let cancelado = false;
        setCarregando(true);
        fornecedorService.listaAutocomplete(termoDebounced.trim())
            .then(res => { if (!cancelado) setResultados(res.data); })
            .catch(() => { if (!cancelado) setResultados([]); })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [isOpen, termoDebounced]);

    function selecionar(fornecedor) {
        onSelecionar(fornecedor);
        onClose();
    }

    function aoCadastrar(fornecedor) {
        setModalCadastroAberto(false);
        selecionar(fornecedor);
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Selecionar Fornecedor"
            subtitle="Busque por Razão Social, Nome Fantasia, CPF ou CNPJ e selecione o fornecedor da solicitação."
            className="w-[90%] min-h-[70vh]"
            footer={
                <div className="flex justify-between">
                    <Button variant="outline" label="Cadastrar Fornecedor" iconLeft="fa fa-plus" onClick={() => setModalCadastroAberto(true)} />
                    <Button variant="outline" label="Fechar" onClick={onClose} />
                </div>
            }
        >
            <div className="p-3 py-4 flex flex-col gap-4">
                <Input
                    type="text"
                    label="Buscar fornecedor"
                    value={termo}
                    onChange={e => setTermo(e.target.value)}
                    placeholder="Razão Social, Nome Fantasia, CPF ou CNPJ"
                    autoFocus
                />

                {termo.trim().length > 0 && termo.trim().length < TAMANHO_MINIMO_BUSCA && (
                    <div className="text-xs text-muted-foreground">Digite pelo menos {TAMANHO_MINIMO_BUSCA} caracteres para buscar.</div>
                )}

                {carregando && (
                    <div className="text-sm text-muted-foreground text-center py-6">Buscando fornecedores...</div>
                )}

                {!carregando && termo.trim().length >= TAMANHO_MINIMO_BUSCA && resultados.length === 0 && (
                    <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground text-center py-6">
                        <span>Nenhum fornecedor encontrado para "{termo.trim()}".</span>
                        <Button variant="outline" label="Cadastrar Fornecedor" iconLeft="fa fa-plus" onClick={() => setModalCadastroAberto(true)} />
                    </div>
                )}

                {!carregando && resultados.length > 0 && (
                    <div className="overflow-y-auto border border-border rounded-lg max-h-[50vh]">
                        <table className="w-full text-xs">
                            <thead className="bg-muted sticky top-0">
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 font-semibold">Razão Social</th>
                                    <th className="px-3 py-2 font-semibold">Nome Fantasia</th>
                                    <th className="px-3 py-2 font-semibold">CPF</th>
                                    <th className="px-3 py-2 font-semibold">CNPJ</th>
                                    <th className="px-3 py-2 w-24" />
                                </tr>
                            </thead>
                            <tbody>
                                {resultados.map(fornecedor => (
                                    <tr
                                        key={fornecedor.id}
                                        onDoubleClick={() => selecionar(fornecedor)}
                                        className="border-t border-border hover:bg-primary/5 cursor-pointer"
                                    >
                                        <td className="px-3 py-2 text-foreground">{fornecedor.nome}</td>
                                        <td className="px-3 py-2 text-foreground">{fornecedor.nomeFantasia}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{fornecedor.cpf ? formatarCnpjCpf(fornecedor.cpf) : ""}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{fornecedor.cnpj ? formatarCnpjCpf(fornecedor.cnpj) : ""}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="blue" label="Selecionar" onClick={() => selecionar(fornecedor)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {resultados.length === 10 && (
                            <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">
                                Mostrando os 10 primeiros resultados — refine a busca para encontrar outros fornecedores.
                            </div>
                        )}
                    </div>
                )}
            </div>

            <CadastrarFornecedorModal
                isOpen={modalCadastroAberto}
                onClose={() => setModalCadastroAberto(false)}
                onCriado={aoCadastrar}
            />
        </Modal>
    );
}
